import { Button, Grid, Group, Paper, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { Line } from "react-chartjs-2";
import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { GET_INSTAGRAM_MEDIA_DETAILS } from "../../services/instagram_analytics";
import {
  AnalyticsEmptyState,
  InstagramAnalyticsHeader,
  StatCard,
  formatDate,
  formatNumber,
  getSyncResultMessage,
  normalizeMetricsHistory,
  parseMaybeNumber,
  resolveMediaLatestMetrics,
  useInstagramAccounts,
  useInstagramSync,
} from "./shared";

const metricKeys = ["impressions", "reach", "likes", "comments", "saves", "shares", "plays"];
const metricLabels: Record<string, string> = {
  impressions: "Impressões",
  reach: "Alcance",
  likes: "Curtidas",
  comments: "Comentários",
  saves: "Salvamentos",
  shares: "Compartilhamentos",
  plays: "Reproduções",
};

export default function InstagramAnalyticsMediaDetailsPage() {
  const navigate = useNavigate();
  const { oauthId, mediaId } = useParams();
  const { accounts, loadingAccounts } = useInstagramAccounts();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [media, setMedia] = React.useState<any>(null);
  const [history, setHistory] = React.useState<any[]>([]);

  const loadData = React.useCallback(async () => {
    if (!oauthId || !mediaId) return;

    try {
      setLoading(true);
      const response = await GET_INSTAGRAM_MEDIA_DETAILS(oauthId, mediaId);
      setMedia(response?.media || null);
      const normalizedHistory = normalizeMetricsHistory(response?.metrics_history || []);
      setHistory(normalizedHistory);
      getSyncResultMessage(normalizedHistory.length === 0);
    } catch (err: any) {
      showNotification({ message: err.message, color: "red" });
    } finally {
      setLoading(false);
    }
  }, [oauthId, mediaId]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

  const latest = history[history.length - 1] || resolveMediaLatestMetrics(media);
  const isEmpty = !loading && !media;

  return (
    <Stack>
      <InstagramAnalyticsHeader
        oauthId={oauthId}
        title="Detalhes da mídia"
        subtitle="Métricas em série temporal da publicação selecionada"
        active="media"
        accounts={accounts}
        loadingAccounts={loadingAccounts}
        syncing={syncing}
        onSync={triggerSync}
      />

      <Group position="apart">
        <Button variant="light" onClick={() => navigate(`/analytics/instagram/${oauthId}/media`)}>
          Voltar para lista de mídias
        </Button>
        {media?.permalink ? (
          <Button component="a" target="_blank" href={media.permalink} rightIcon={<FaExternalLinkAlt />}>
            Abrir no Instagram
          </Button>
        ) : null}
      </Group>

      {isEmpty ? (
        <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
      ) : (
        <>
          <Grid>
            <Grid.Col md={5}>
              <Paper withBorder p="md">
                {media?.media_url ? (
                  media?.media_type === "VIDEO" || media?.media_type === "REEL" ? (
                    <video controls style={{ width: "100%", maxHeight: 420 }} src={media.media_url} />
                  ) : (
                    <img src={media.media_url} alt="Publicação" style={{ width: "100%", maxHeight: 420, objectFit: "cover" }} />
                  )
                ) : (
                  <Text c="dimmed">Prévia da mídia indisponível</Text>
                )}

                <Text mt="sm" size="sm" c="dimmed">
                  {formatDate(media?.timestamp)}
                </Text>
                <Text size="sm" mt="xs">
                  {media?.caption || "Sem legenda"}
                </Text>
              </Paper>
            </Grid.Col>

            <Grid.Col md={7}>
              <Paper withBorder p="md">
                <Line
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: "bottom" as const },
                    },
                  }}
                  data={{
                    labels: history.map((item) => item.date),
                    datasets: metricKeys.map((key, index) => ({
                      label: metricLabels[key] || key,
                      data: history.map((item) => parseMaybeNumber(item[key])),
                      borderColor: [
                        "#2563eb",
                        "#9333ea",
                        "#16a34a",
                        "#e11d48",
                        "#d97706",
                        "#0891b2",
                        "#6b7280",
                      ][index],
                      backgroundColor: "transparent",
                      spanGaps: true,
                      tension: 0.3,
                    })),
                  }}
                />
              </Paper>
            </Grid.Col>
          </Grid>

          <Grid>
            {metricKeys.map((key) => (
              <Grid.Col key={key} md={3}>
                <StatCard label={metricLabels[key] || key} value={formatNumber(parseMaybeNumber(latest?.[key]))} />
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}
    </Stack>
  );
}
